import { HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { faEnvelope, faMessage } from '@fortawesome/free-regular-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { BehaviorSubject, Observable, filter, take, map, Subscription } from 'rxjs';
import { AccountDeletionRequestInterface } from '../../../models/interface/account-deletion-request.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { AlertService } from '../../../services/alert/alert.service';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { AuthenticationStore } from '../../../store/authentication/component-store/authentication.store';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { Store } from '@ngrx/store';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { Router } from '@angular/router';

@Component({
  selector: 'app-card-user',
  templateUrl: './card-user.component.html',
  styleUrls: ['./card-user.component.css'],
  providers: [AuthenticationStore],
})
export class CardUserComponent implements OnInit {
  @Output()
  public emitDelete = new EventEmitter<void>();

  @Output()
  public emitUpdate = new EventEmitter<void>();

  @Output()
  public emitReloadInvitations = new EventEmitter<void>();

  // Icons
  faCheck = faCheck;
  faTimes = faTimes;
  faPen = faPen;
  faTrash = faTrash;
  faMessage = faMessage;
  faTimesCircle = faTimesCircle;
  faCheckCircle = faCheckCircle;
  faEnvelope = faEnvelope;

  public user: UserInterface;
  public deletionRequests: AccountDeletionRequestInterface[] = [];

  public isAdmin = false;
  public hasPendingInvitations = false;

  public userAsPatient = false;

  public resendVerificationResponse = new BehaviorSubject<string>('DEFAULT');
  public resendVerificationResponse$: Observable<any>;

  public profile$: Observable<ProfileInterface>;

  private myUserId: number = null;

  private subscriptions: Subscription[] = [];

  private isModalLoadingSubject = new BehaviorSubject<boolean>(true);

  constructor(
    private router: Router,
    private helperDialogService: HelperDialogService,
    private alertService: AlertService,
    private authenticationStore: AuthenticationStore,
    private store: Store<{
      myProfile: ProfileInterface;
    }>,
  ) {
    this.profile$ = this.store.select('myProfile');
    this.resendVerificationResponse$ = this.authenticationStore.resendVerificationResponse$;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  @Input()
  set _user(_user: UserInterface) {
    if (_user) {
      this.user = _user;
    }
  }

  @Input()
  set _deletionRequests(_deletionRequests: AccountDeletionRequestInterface[]) {
    if (_deletionRequests) {
      this.deletionRequests = _deletionRequests;
    }
  }

  @Input()
  set _isAdmin(_isAdmin: boolean) {
    if (_isAdmin !== undefined) {
      this.isAdmin = _isAdmin;
    }
  }

  @Input()
  set _hasPendingInvitations(_hasPendingInvitations: boolean) {
    if (_hasPendingInvitations !== undefined) {
      this.hasPendingInvitations = _hasPendingInvitations;
    }
  }

  @Input()
  set _userAsPatient(_userAsPatient: boolean) {
    if (_userAsPatient !== undefined) {
      this.userAsPatient = _userAsPatient;
    }
  }

  ngOnInit(): void {
    // Get user's id
    this.subscriptions.push(
      this.profile$
        .pipe(
          filter((user) => user !== null),
          take(1),
        )
        .subscribe((result: ProfileInterface) => {
          this.myUserId = result.id;
        }),
    );
  }

  public isUserInRole(user: UserInterface, slug: string): boolean {
    return user.attributes.roles.some((role) => role.slug.toString() === slug);
  }

  public hasDeletionRequest(userId: number): boolean {
    const userIdStr = userId.toString();
    return this.deletionRequests.some(
      (request) => request.attributes.user_id.toString() === userIdStr,
    );
  }

  public resendVerificationEmail(email: string): void {
    if (this.resendVerificationResponse.value === 'DEFAULT') {
      this.isModalLoadingSubject.next(true);
      this.resendVerificationResponse.next('LOADING');

      this.authenticationStore.resendVerification(email);
      this.resendVerificationResponse$
        .pipe(
          filter((value) => !!value),
          take(1),
        )
        .subscribe((value) => {
          if (value instanceof HttpResponse) {
            this.alertService.success('ACCOUNT_RESEND_SUCCEEDED');
            this.resendVerificationResponse.next('SUCCESS');
          } else {
            this.alertService.error('ACCOUNT_RESEND_FAILED');
            this.resendVerificationResponse.next('FAILURE');
          }
          this.isModalLoadingSubject.next(false);
          setTimeout(() => {
            this.resendVerificationResponse.next('DEFAULT');
          }, 2500);
          this.openDialogVerification();
        });
    }
  }

  public openDialogUserUpdate(): void {
    this.helperDialog
      .openDialogUserUpdate(this.user)
      .afterClosed()
      .subscribe(() => {
        this.emitUpdate.emit();
      });
  }

  public openDialogUserDelete(): void {
    this.helperDialog
      .openDialogUserDelete(this.user)
      .afterClosed()
      .subscribe(() => {
        this.emitDelete.emit();
      });
  }

  public openDialogVerification(): void {
    const dialogRef = this.helperDialog.openDialogVerification(this.isModalLoadingSubject);
    const loading = this.isModalLoadingSubject.asObservable().subscribe(value => {
      if (dialogRef && dialogRef.componentInstance) {
        dialogRef.componentInstance.data = { submitted: value };
      }
    });
    dialogRef.afterClosed().pipe(map(() => loading.unsubscribe()));
  }

  public openDialogAccountRequests(): void {
    this.helperDialog
      .openDialogAccountRequests(this.user)
      .afterClosed()
      .subscribe(() => {});
  }

  public openDialogInvitation(user: UserInterface): void {
    this.helperDialog
      .openDialogPatientInvitationCheck(user, this.myUserId)
      .afterClosed()
      .subscribe(() => this.emitReloadInvitations.emit());
  }

  public openDialogStudyInvite(selectedUser: UserInterface): void {
    this.helperDialog
      .openDialogAccountInterventionInstanceAssignment('patientStudyInvitation', this.myUserId, undefined, selectedUser)
      .afterClosed()
      .subscribe(() => this.emitReloadInvitations.emit());
  }

  public openDialogInstanceCreate(selectedUser: UserInterface): void {
    this.helperDialog
      .openDialogAccountInterventionInstanceAssignment('patientInterventionAssignment', this.myUserId, undefined, selectedUser)
      .afterClosed()
      .subscribe(() => {});
  }

  public openDialogDeleteUnverified(user: UserInterface): void {
    this.helperDialog
      .openDialogUnverifiedAccountDelete(user, this.myUserId)
      .afterClosed()
      .subscribe(() => this.emitDelete.emit());
  }

  public getDetailedPatient(id?: number): void {
    this.router.navigateByUrl(`/patients/${id}`);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
